import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {SearchIcon} from '@primer/octicons-react'
import {Button, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {partition} from '../../../../utils/partition'
import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {ItemType} from '../../../api/memex-items/item-type'
import type {RepositoryItem, SuggestedRepository} from '../../../api/repository/contracts'
import {BulkAddSidePanelAddItems} from '../../../api/stats/contracts'
import {isCopilot} from '../../../helpers/assignee-helper'
import {shortcutFromEvent, SHORTCUTS} from '../../../helpers/keyboard-shortcuts'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useAutoAddSubIssueWorkflowToast} from '../../../hooks/use-auto-add-sub-issue-toast'
import {useSidePanel} from '../../../hooks/use-side-panel'
import {Resources} from '../../../strings'
import type {OmnibarItemAttrs} from '../../omnibar/types'
import {useCopilotAssignmentModal} from '../../react_table/copilot-assignment-modal-provider'
import {SidePanelToolbar} from '../header'
import {ItemSuggestionsList} from '../item-suggestions-list'
import {RepoSuggestions} from '../repo-suggestions'
import {useBulkAddItems} from './bulk-add-items-provider'
import styles from './bulk-add-view.module.css'

const SearchButton = memo(function SearchButton({onClick, disabled}: {onClick: () => void; disabled: boolean}) {
  return (
    <Button
      variant="invisible"
      size="small"
      onClick={onClick}
      aria-label="Search"
      disabled={disabled}
      className={clsx(styles.statusButton, disabled && styles.statusButtonDisabled)}
      {...testIdProps('bulk-add-search-button')}
    >
      Search ⏎
    </Button>
  )
})

type BulkAddViewProps = {
  targetRepository?: SuggestedRepository
  newItemAttributes?: OmnibarItemAttrs
}

const isIssue = (item: RepositoryItem) => item.type === ItemType.Issue

const issueHasCopilotAssigned = (item: RepositoryItem) => {
  if ('assignees' in item) {
    return item.assignees?.some(a => a.login && isCopilot(a.login)) ?? false
  }
  return false
}

export const BulkAddView = ({targetRepository, newItemAttributes}: BulkAddViewProps) => {
  const {postStats} = usePostStats()
  const {isPaneOpened} = useSidePanel()
  const {
    addSelectedItemsRequest,
    selectedItems,
    hasFetchedRepos,
    selectedRepo,
    setSelectedRepo,
    setHasFetchedRepos,
    loading,
    addingItems,
    items,
    searchQuery,
    setSearchQuery,
    setAreAllItemsAdded,
    setUpdateColumnsAction,
    MAX_ITEM_NUM,
  } = useBulkAddItems()
  const [searchHasFocus, setSearchHasFocus] = useState(false)
  const [hasSearchInput, setHasSearchInput] = useState(false)
  const [currentSearchValue, setCurrentSearchValue] = useState(searchQuery)
  const hasRepos = hasFetchedRepos && selectedRepo
  const inputRef = useRef<HTMLInputElement>(null)

  const wasPaneOpened = useRef(isPaneOpened)
  useEffect(
    function resetStateOnClose() {
      if (wasPaneOpened.current && !isPaneOpened) {
        setSelectedRepo(null)
        setSearchQuery('')
        setUpdateColumnsAction([])
        setHasFetchedRepos(false)
      }
      wasPaneOpened.current = isPaneOpened
    },
    [setHasFetchedRepos, setSearchQuery, setSelectedRepo, setUpdateColumnsAction, isPaneOpened],
  )

  const onSearchInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const shortcut = shortcutFromEvent(e)
      const query = e.currentTarget.value.trim()
      if (shortcut === SHORTCUTS.ENTER) {
        setSearchQuery(query)
      }
    },
    [setSearchQuery],
  )

  const searchInputProps = useIgnoreKeyboardActionsWhileComposing(onSearchInputKeyDown)

  const onSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentSearchValue(e.currentTarget.value)
    setHasSearchInput(e.currentTarget.value.trim().length > 0)
  }, [])

  const onSearchButtonClick = useCallback(() => {
    const query = inputRef.current?.value ?? ''
    setSearchQuery(query)
  }, [setSearchQuery])

  const onSearchInputFocus = useCallback(() => {
    setSearchHasFocus(true)
  }, [])

  const onSearchInputBlur = useCallback(() => {
    setSearchHasFocus(false)
  }, [])

  useEffect(() => {
    setUpdateColumnsAction(newItemAttributes?.updateColumnActions ?? [])
  }, [setUpdateColumnsAction, newItemAttributes])

  const showAutoAddSubIssueToast = useAutoAddSubIssueWorkflowToast()

  const {openModal} = useCopilotAssignmentModal()

  const isGroupedByCopilotAssignee = useMemo(() => {
    const actions = newItemAttributes?.updateColumnActions
    if (!actions) return false
    const assigneesAction = actions.find(a => a.dataType === MemexColumnDataType.Assignees)
    return !assigneesAction ? false : assigneesAction.value.some(u => isCopilot(u.login))
  }, [newItemAttributes?.updateColumnActions])

  const performAddSelectedItems = useCallback(
    async (itemsToAdd = selectedItems) => {
      await addSelectedItemsRequest.perform(itemsToAdd)

      if (itemsToAdd.some(item => 'hasSubIssues' in item && item.hasSubIssues)) {
        showAutoAddSubIssueToast()
      }

      postStats({name: BulkAddSidePanelAddItems, context: itemsToAdd.length.toString()})
      // if there are less items than 25, there will be no "refill" coming in anyway
      if (itemsToAdd === selectedItems && itemsToAdd.length === MAX_ITEM_NUM) {
        setAreAllItemsAdded(true)
      }
    },
    [addSelectedItemsRequest, selectedItems, postStats, MAX_ITEM_NUM, showAutoAddSubIssueToast, setAreAllItemsAdded],
  )

  const addSelectedItems = useCallback(async () => {
    const [issuesWithoutCopilotAssigned, remainingItems] = partition(
      selectedItems,
      item => isIssue(item as RepositoryItem) && !issueHasCopilotAssigned(item as RepositoryItem),
    )

    const canUseCopilotModal =
      selectedRepo &&
      isGroupedByCopilotAssignee &&
      isFeatureEnabled('issues_copilot_cross_repo_assign') &&
      issuesWithoutCopilotAssigned.length > 0

    if (canUseCopilotModal) {
      const issueNumbersWithoutCopilotAssigned = issuesWithoutCopilotAssigned.map(i => i.number)

      if (issueNumbersWithoutCopilotAssigned.length > 0) {
        const repository: CopilotAssignmentRepository = {
          name: selectedRepo.name,
          owner: {login: selectedRepo.owner},
        }

        // Add non-modal items immediately so users aren't blocked waiting for Copilot assignment to finish.
        if (remainingItems.length > 0) {
          void performAddSelectedItems(remainingItems)
        }

        openModal(repository, issueNumbersWithoutCopilotAssigned, async () => {
          await performAddSelectedItems(issuesWithoutCopilotAssigned)
        })
        return
      }
      await performAddSelectedItems()
      return
    }

    await performAddSelectedItems()
  }, [isGroupedByCopilotAssignee, openModal, performAddSelectedItems, selectedItems, selectedRepo])

  const searchButtonVisible = hasSearchInput || searchHasFocus || searchQuery !== currentSearchValue // the last one is for when the user deletes the last executed search
  const searchButtonDisabled = (!hasSearchInput && !searchQuery) || searchQuery === currentSearchValue

  return (
    <div className={styles.bulkAddContainer} {...testIdProps('bulk-add-view')}>
      <div className={styles.bulkAddContent}>
        <div className={styles.bulkAddHeader}>
          <SidePanelToolbar showCloseButton />
          <div className={styles.headerSpacer} />
          <h2 className={styles.bulkAddTitle}>{Resources.sidePanelBulkAddLabel}</h2>
        </div>
        <div className={styles.searchContainer}>
          <RepoSuggestions key="repoSuggestions" targetRepository={targetRepository} />
          <TextInput
            disabled={!hasRepos}
            leadingVisual={SearchIcon}
            aria-label="Search for issues and pull requests"
            ref={inputRef}
            type="text"
            placeholder="Search for issues and pull requests"
            onChange={onSearchInputChange}
            onFocus={onSearchInputFocus}
            onBlur={onSearchInputBlur}
            {...searchInputProps}
            size="small"
            value={currentSearchValue}
            trailingAction={
              searchButtonVisible ? (
                <SearchButton onClick={onSearchButtonClick} disabled={searchButtonDisabled} />
              ) : undefined
            }
            className={styles.searchInput}
            {...testIdProps('suggested-items-search-input')}
          />
        </div>
        <ItemSuggestionsList />
      </div>
      <div className={styles.bulkAddFooter}>
        <Button
          variant="primary"
          onClick={addSelectedItems}
          {...testIdProps('bulk-add-button')}
          disabled={loading || addingItems || items?.length === 0 || selectedItems.length === 0}
          className={styles.addButton}
        >
          {!addingItems ? 'Add selected items' : selectedItems.length === 1 ? 'Adding item...' : 'Adding items...'}
        </Button>
      </div>
    </div>
  )
}

try{ SearchButton.displayName ||= 'SearchButton' } catch {}
try{ BulkAddView.displayName ||= 'BulkAddView' } catch {}